import { useCallback } from "react";
import { useAttributePromotionPreflightLazyQuery } from "#graphql/supergraphHooks";
import { apiPreflightToSummary } from "../mappers";
import type { PromotionPreflightSummary } from "#common/types/vault";

export interface UsePromotionPreflightReturn {
  /**
   * Run the promotion pre-check for an attribute (DV-499). Pass `label` to
   * re-check a proposed rename instead of the attribute's stored label.
   *
   * Resolves `null` on an unknown attribute id (the server's contract) AND on
   * any transport/GraphQL error — fail open, mirroring the contract documented
   * in `useAttributeNameConflict`: the pre-check is advisory UX, and the
   * `promoteAttributeToGlobal` mutation remains the enforcement point (it
   * rejects `ATTRIBUTE_LABEL_CONFLICT` regardless of what the UI showed).
   */
  preflightPromotion: (
    attributeId: string,
    label?: string,
  ) => Promise<PromotionPreflightSummary | null>;
  loading: boolean;
}

/**
 * Lazy wrapper around the `attributePromotionPreflight` query. Lazy because
 * the check runs per promote click, not on mount; `no-cache` because a
 * pre-check must reflect the catalog as of this moment (repo convention for
 * admin catalog reads).
 */
export const usePromotionPreflight = (): UsePromotionPreflightReturn => {
  const [runQuery, { loading }] = useAttributePromotionPreflightLazyQuery({
    fetchPolicy: "no-cache",
  });

  const preflightPromotion = useCallback(
    async (attributeId: string, label?: string): Promise<PromotionPreflightSummary | null> => {
      try {
        const { data, error } = await runQuery({
          variables: { id: attributeId, ...(label !== undefined ? { label } : {}) },
        });
        if (error) return null;
        const payload = data?.attributePromotionPreflight;
        return payload ? apiPreflightToSummary(payload) : null;
      } catch {
        return null;
      }
    },
    [runQuery],
  );

  return { preflightPromotion, loading };
};
